import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import PWAInstallPrompt from "@/components/site/PWAInstallPrompt";
import AutoPushSubscribe from "@/components/push/AutoPushSubscribe";
import ProtectedRoute from "@/components/site/ProtectedRoute";

import Home from "@/pages/public/Home";
import Gallery from "@/pages/public/Gallery";
import Blogs from "@/pages/public/Blogs";
import BlogDetail from "@/pages/public/BlogDetail";
import Booking from "@/pages/public/Booking";
import Tournaments from "@/pages/public/Tournaments";
import TournamentDetail from "@/pages/public/TournamentDetail";

import ParentAuth from "@/pages/parent/ParentAuth";
import ParentDashboard from "@/pages/parent/ParentDashboard";
import ParentChangePassword from "@/pages/parent/ChangePassword";
import GymMembership from "@/pages/parent/GymMembership";
import ParentMemberships from "@/pages/parent/ParentMemberships";

import CoachLogin from "@/pages/coach/CoachLogin";
import CoachLayout from "@/pages/coach/CoachLayout";
import CoachDashboard from "@/pages/coach/CoachDashboard";
import CoachBatchDetail from "@/pages/coach/CoachBatchDetail";
import CoachChangePassword from "@/pages/coach/CoachChangePassword";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminBootstrap from "@/pages/AdminBootstrap";
import PaymentResult from "@/pages/PaymentResult";
import ManagementLogin from "@/pages/management/ManagementLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import SlotMatrix from "@/pages/admin/SlotMatrix";
import AdminParents from "@/pages/admin/AdminParents";
import AdminWards from "@/pages/admin/AdminWards";
import AdminHostelSettings from "@/pages/admin/AdminHostelSettings";
import MemberLogin from "@/pages/MemberLogin";
import MemberSignup from "@/pages/MemberSignup";
import MemberPortal from "@/pages/MemberPortal";
import MemberProfile from "@/pages/MemberProfile";
import MemberForgotPassword from "@/pages/MemberForgotPassword";
import MemberResetPassword from "@/pages/MemberResetPassword";
import AdminStaff from "@/pages/admin/AdminStaff";
import AdminChangePassword from "@/pages/admin/AdminChangePassword";
import AdminAttendance from "@/pages/admin/AdminAttendance";
import AdminBatches from "@/pages/admin/AdminBatches";
import AdminBlogs from "@/pages/admin/AdminBlogs";
import AdminGallery from "@/pages/admin/AdminGallery";
import AdminTestimonials from "@/pages/admin/AdminTestimonials";
import AdminNews from "@/pages/admin/AdminNews";
import AdminHomepage from "@/pages/admin/AdminHomepage";
import AdminFeeSheets from "@/pages/admin/AdminFeeSheets";
import AdminLogs from "@/pages/admin/AdminLogs";
import AdminTournaments from "@/pages/admin/AdminTournaments";
import AdminTournamentControl from "@/pages/admin/AdminTournamentControl";
import AdminGymScanner from "@/pages/admin/AdminGymScanner";
import AdminSportsIngest from "@/pages/admin/AdminSportsIngest";
import AdminTeam from "@/pages/admin/AdminTeam";
import AdminContactMessages from "@/pages/admin/AdminContactMessages";
import AdminPricingRules from "@/pages/admin/AdminPricingRules";
import AdminServicePricing from "@/pages/admin/AdminServicePricing";
import AdminEventBilling from "@/pages/admin/AdminEventBilling";
import AdminNotifications from "@/pages/admin/AdminNotifications";
import AdminAccounting from "@/pages/admin/AdminAccounting";
import AdminKitSales from "@/pages/admin/AdminKitSales";
import AdminCashReconcile from "@/pages/admin/AdminCashReconcile";
import AdminLedgers from "@/pages/admin/AdminLedgers";
import AdminSuppliers from "@/pages/admin/AdminSuppliers";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminAuditLog from "@/pages/admin/AdminAuditLog";
import AdminCoaches from "@/pages/admin/AdminCoaches";
import AdminDayClose from "@/pages/admin/AdminDayClose";
import AdminPaymentConfig from "@/pages/admin/AdminPaymentConfig";
import AdminPhonePeReport from "@/pages/admin/AdminPhonePeReport";
import AdminNgoProfile from "@/pages/admin/AdminNgoProfile";
import AdminTransactions from "@/pages/admin/AdminTransactions";
import AdminDonations from "@/pages/admin/AdminDonations";
import AdminMembershipPlans from "@/pages/admin/AdminMembershipPlans";
import AdminMembers from "@/pages/admin/AdminMembers";
import AdminDowntimes from "@/pages/admin/AdminDowntimes";
import AdminStaffReserve from "@/pages/admin/AdminStaffReserve";
import AdminMessageProfile from "@/pages/admin/AdminMessageProfile";
import AdminWhatsappBridge from "@/pages/admin/AdminWhatsappBridge";
import AdminWhatsappCampaigns from "@/pages/admin/AdminWhatsappCampaigns";
import AdminStarAssist from "@/pages/admin/AdminStarAssist";
import AdminContactsDirectory from "@/pages/admin/AdminContactsDirectory";
import AdminDeskCheckin from "@/pages/admin/AdminDeskCheckin";
import Contact from "@/pages/public/Contact";
import EnrolWard from "@/pages/public/EnrolWard";
import Donate from "@/pages/public/Donate";
import Academy from "@/pages/public/Academy";
import AcademySection from "@/pages/public/AcademySection";
import FeesStructure from "@/pages/public/FeesStructure";
import Membership from "@/pages/public/Membership";
import MemberAuth from "@/pages/public/MemberAuth";
import MemberDashboard from "@/pages/public/MemberDashboard";
import AdminAcademyContent from "@/pages/admin/AdminAcademyContent";
import AdminAdmission from "@/pages/admin/AdminAdmission";
import Admission from "@/pages/public/Admission";
import Team from "@/pages/public/Team";
import { PaymentSuccess, PaymentCancel } from "@/pages/public/PaymentResult";
import GoogleAuthCallback from "@/pages/GoogleAuthCallback";
import { HubMemberProvider } from "@/lib/HubMemberContext";
import MemberGate from "@/components/MemberGate";
import StarAssist from "@/components/StarAssist";

import "@/App.css";

function PublicShell({ children }) {
  return (
    <>
      <Navbar />
      {children}
      <Footer />
    </>
  );
}

/**
 * Intercepts fragment-based session_id BEFORE the normal router matches.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <PublicShell><GoogleAuthCallback destination={location.pathname || "/"} /></PublicShell>;
  }
  return <AppRoutes />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <HubMemberProvider>
            <Toaster theme="dark" position="top-right" richColors />
            <PWAInstallPrompt />
            <AutoPushSubscribe />
            <StarAssist />
            <AppRouter />
          </HubMemberProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

function AppRoutes() {
  return (
          <Routes>
            {/* Public routes with navbar/footer */}
            <Route path="/" element={<PublicShell><Home /></PublicShell>} />
            <Route path="/gallery" element={<PublicShell><Gallery /></PublicShell>} />
            <Route path="/blogs" element={<PublicShell><Blogs /></PublicShell>} />
            <Route path="/blogs/:slug" element={<PublicShell><BlogDetail /></PublicShell>} />
            <Route path="/booking" element={<PublicShell><MemberGate purpose="book a slot" returnTo="/booking"><Booking /></MemberGate></PublicShell>} />
            <Route path="/tournaments" element={<PublicShell><Tournaments /></PublicShell>} />
            <Route path="/tournaments/:id" element={<PublicShell><TournamentDetail /></PublicShell>} />
            <Route path="/payment/success" element={<PublicShell><PaymentSuccess /></PublicShell>} />
            <Route path="/payment/cancel" element={<PublicShell><PaymentCancel /></PublicShell>} />
            <Route path="/contact" element={<PublicShell><Contact /></PublicShell>} />
            <Route path="/enrol" element={<PublicShell><EnrolWard /></PublicShell>} />
            <Route path="/enroll" element={<PublicShell><EnrolWard /></PublicShell>} />
            <Route path="/donate" element={<PublicShell><Donate /></PublicShell>} />
            <Route path="/academy" element={<PublicShell><Academy /></PublicShell>} />
            <Route path="/academy/fees-structure" element={<PublicShell><FeesStructure /></PublicShell>} />
            <Route path="/academy/:slug" element={<PublicShell><AcademySection /></PublicShell>} />
            <Route path="/admission" element={<PublicShell><Admission /></PublicShell>} />
            <Route path="/team" element={<PublicShell><Team /></PublicShell>} />

            {/* Membership (public) */}
            <Route path="/membership" element={<PublicShell><MemberGate purpose="get a membership" returnTo="/membership"><Membership /></MemberGate></PublicShell>} />
            <Route
              path="/member"
              element={
                <ProtectedRoute roles={["member"]}>
                  <PublicShell><MemberDashboard /></PublicShell>
                </ProtectedRoute>
              }
            />

            {/* Legacy parent routes → redirect to unified Member Portal */}
            <Route path="/parent/login" element={<Navigate to="/member/login" replace />} />
            <Route path="/parent" element={<Navigate to="/me" replace />} />
            {/* Kept for change-password + memberships/gym compatibility */}
            <Route
              path="/parent/change-password"
              element={
                <ProtectedRoute roles={["parent"]}>
                  <PublicShell><ParentChangePassword /></PublicShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/parent/gym"
              element={
                <ProtectedRoute roles={["parent", "admin", "staff"]}>
                  <PublicShell><GymMembership /></PublicShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/parent/memberships"
              element={
                <ProtectedRoute roles={["parent", "admin", "staff"]}>
                  <PublicShell><ParentMemberships /></PublicShell>
                </ProtectedRoute>
              }
            />

            {/* Member portal (hub members via Google OR email/password) */}
            <Route path="/member/login" element={<PublicShell><MemberLogin /></PublicShell>} />
            <Route path="/member/signup" element={<PublicShell><MemberSignup /></PublicShell>} />
            <Route path="/member/forgot-password" element={<PublicShell><MemberForgotPassword /></PublicShell>} />
            <Route path="/member/reset-password" element={<PublicShell><MemberResetPassword /></PublicShell>} />
            <Route path="/me" element={<PublicShell><MemberPortal /></PublicShell>} />
            <Route path="/member/profile" element={<PublicShell><MemberProfile /></PublicShell>} />
            <Route path="/payment-result" element={<PublicShell><PaymentResult /></PublicShell>} />

            {/* Admin & Staff ERP */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin-reset" element={<AdminBootstrap />} />
            <Route path="/management/login" element={<ManagementLogin />} />

            {/* Coach portal */}
            <Route path="/coach/login" element={<CoachLogin />} />
            <Route
              path="/coach/change-password"
              element={
                <ProtectedRoute roles={["coach"]}>
                  <CoachLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<CoachChangePassword />} />
            </Route>
            <Route
              path="/coach"
              element={
                <ProtectedRoute roles={["coach"]}>
                  <CoachLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<CoachDashboard />} />
              <Route path="batches/:batchId" element={<CoachBatchDetail />} />
            </Route>
            <Route
              path="/admin"
              element={
                <ProtectedRoute roles={["admin", "staff"]}>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="slot-matrix" element={<SlotMatrix />} />
              <Route path="parents" element={<AdminParents />} />
              <Route path="wards" element={<AdminWards kind="academic" />} />
              <Route path="wards/hostel" element={<AdminWards kind="hostel" />} />
              <Route path="hostel-settings" element={<AdminHostelSettings />} />
              <Route path="staff" element={<AdminStaff />} />
              <Route path="coaches" element={<AdminCoaches />} />
              <Route path="day-close" element={<AdminDayClose />} />
              <Route path="change-password" element={<AdminChangePassword />} />
              <Route path="attendance" element={<AdminAttendance />} />
              <Route path="batches" element={<AdminBatches />} />
              <Route path="blogs" element={<AdminBlogs />} />
              <Route path="gallery" element={<AdminGallery />} />
              <Route path="testimonials" element={<AdminTestimonials />} />
              <Route path="news" element={<AdminNews />} />
              <Route path="homepage" element={<AdminHomepage />} />
              <Route path="fee-sheets" element={<AdminFeeSheets />} />
              <Route path="tournaments" element={<AdminTournaments />} />
              <Route path="tournaments/:id" element={<AdminTournamentControl />} />
              <Route path="gym-scanner" element={<AdminGymScanner />} />
              <Route path="sports-ingest" element={<AdminSportsIngest />} />
              <Route path="team" element={<AdminTeam />} />
              <Route path="contact-messages" element={<AdminContactMessages />} />
              <Route path="pricing-rules" element={<AdminPricingRules />} />
              <Route path="service-pricing" element={<AdminServicePricing />} />
              <Route path="event-billing" element={<AdminEventBilling />} />
              <Route path="notifications" element={<AdminNotifications />} />
              <Route path="accounting" element={<AdminAccounting />} />
              <Route path="kit-sales" element={<AdminKitSales />} />
              <Route path="cash-reconcile" element={<AdminCashReconcile />} />
              <Route path="ledgers" element={<AdminLedgers />} />
              <Route path="suppliers" element={<AdminSuppliers />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="audit-log" element={<AdminAuditLog />} />
              <Route path="logs" element={<AdminLogs />} />
              <Route path="payment-config" element={<AdminPaymentConfig />} />
              <Route path="phonepe-report" element={<AdminPhonePeReport />} />
              <Route path="ngo-profile" element={<AdminNgoProfile />} />
              <Route path="transactions" element={<AdminTransactions />} />
              <Route path="donations" element={<AdminDonations />} />
              <Route path="academy-content" element={<AdminAcademyContent />} />
              <Route path="admission" element={<AdminAdmission />} />
              <Route path="membership-plans" element={<AdminMembershipPlans />} />
              <Route path="members" element={<AdminMembers />} />
              <Route path="downtimes" element={<AdminDowntimes />} />
              <Route path="staff-reserve" element={<AdminStaffReserve />} />
              <Route path="message-profile" element={<AdminMessageProfile />} />
              <Route path="whatsapp/bridge" element={<AdminWhatsappBridge />} />
              <Route path="whatsapp/campaigns" element={<AdminWhatsappCampaigns />} />
              <Route path="star-assist" element={<AdminStarAssist />} />
              <Route path="contacts" element={<AdminContactsDirectory />} />
              <Route path="desk-checkin" element={<AdminDeskCheckin />} />
            </Route>
          </Routes>
  );
}

export default App;
